import { useState } from "react";
import { Fab, Popover, Typography } from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import CheckIcon from "@mui/icons-material/Check";

export interface AppItem {
  name: string;
  url: string | null;
  initials: string;
  color: string;
  active?: boolean;
  sendReferrer?: boolean;
}

export const DEFAULT_APPS: AppItem[] = [
  { name: "ERP", url: null, initials: "E", color: "#FF6638", active: true },
  { name: "xauto.ro", url: "https://xauto.ro/masini-de-vanzare-second-hand-ieftine", initials: "X", color: "#4D5E80", sendReferrer: true },
  { name: "bid.xauto.ro", url: "https://bid.xauto.ro", initials: "B", color: "#2196F3" },
];

interface AppLauncherProps {
  apps?: AppItem[];
}

export default function AppLauncher({ apps = DEFAULT_APPS }: AppLauncherProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAppClick = (app: AppItem) => {
    if (!app.url) return handleClose();

    if (app.sendReferrer) {
      // Use an anchor click to preserve the HTTP Referrer header
      const anchor = document.createElement("a");
      anchor.href = app.url;
      anchor.target = "_blank";
      anchor.rel = "noopener"; // noopener only — keeps referrer intact
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } else {
      window.open(app.url, "_blank", "noopener,noreferrer");
    }

    handleClose();
  };

  return (
    <>
      <Fab
        onClick={handleOpen}
        size="small"
        style={{ backgroundColor: "#fff", color: "#4D5E80", marginRight: 8 }}
        aria-label="app launcher"
        aria-controls={open ? "app-launcher-popover" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <AppsIcon />
      </Fab>

      <Popover
        id="app-launcher-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: "16px",
            mt: 1,
            overflow: "hidden",
            minWidth: 280,
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px 8px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Typography
            style={{ fontSize: 13, color: "#5f6368", fontWeight: 500, letterSpacing: 0.2 }}
          >
            Aplicații
          </Typography>
        </div>

        {/* App grid */}
        <div
          style={{
            padding: "12px 8px 16px",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
          }}
        >
          {apps.map((app) => (
            <div
              key={app.name}
              onClick={() => handleAppClick(app)}
              onMouseEnter={() => setHoveredApp(app.name)}
              onMouseLeave={() => setHoveredApp(null)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "14px 8px 10px",
                borderRadius: 12,
                cursor: app.url ? "pointer" : "default",
                gap: 8,
                backgroundColor: hoveredApp === app.name ? "#f1f3f4" : "transparent",
                transition: "background-color 0.1s ease",
              }}
            >
              {/* Icon with active check badge */}
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    backgroundColor: app.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#fff",
                    boxShadow: app.active
                      ? `0 0 0 3px #fff, 0 0 0 5px ${app.color}`
                      : "none",
                  }}
                >
                  {app.initials}
                </div>
                {app.active && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: -2,
                      right: -2,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      backgroundColor: app.color,
                      border: "2px solid #fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckIcon style={{ fontSize: 11, color: "#fff" }} />
                  </div>
                )}
              </div>

              {/* App name */}
              <Typography
                style={{
                  fontSize: 11,
                  color: "#3c4043",
                  textAlign: "center",
                  fontWeight: app.active ? 600 : 400,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  maxWidth: 72,
                }}
              >
                {app.name}
              </Typography>
            </div>
          ))}
        </div>
      </Popover>
    </>
  );
}
